// BookingDataProvider - Core timeZone-aware booking state management
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
} from "react";
import meetingTypeService from "../services/meetingTypeService";
import bookingService from "../services/bookingService";
import { MeetingType } from "../services/meetingTypeService";
import { TimeSlot, BookingFormData, CachedSlotData } from "../types/booking";
import { getUserTimezone } from "../modules/timezone";
import { utcToZonedTime } from "date-fns-tz";
import axios from "axios";
import { parseISO } from "date-fns";
import TimezoneService from '../utils/TimezoneService';
import { generateCacheKey } from "../utils/cacheKeyGenerator";

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Helper function to get auth headers
const getAuthHeaders = (token?: string | null) => {
  // Use provided token, or fall back to localStorage token
  const authToken = token || localStorage.getItem('token');
  if (authToken) {
    return { Authorization: `Bearer ${authToken}` };
  }
  return {};
};

// Helper function to handle API errors
const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  }
  return "An unknown error occurred";
};

// generateCacheKey function moved to shared utility: utils/cacheKeyGenerator.ts

interface BookingDataContextType {
  // Meeting type data
  meetingType: MeetingType | null;
  loadingMeetingType: boolean;
  meetingTypeError: string | null;

  // Slots loading state
  loadingSlots: boolean;
  loadingInitialCalendar: boolean;
  slotsError: string | null;

  // Cached slot data
  cachedMonthSlots: Record<string, CachedSlotData>;

  // First available month (for calendar initial display)
  firstAvailableMonth: Date | null;

  // Booking state
  bookingInProgress: boolean;
  bookingSuccess: boolean;
  bookingError: string | null;
  bookingResponse: any;

  // Methods
  fetchMeetingType: (
    username: string,
    slug: string
  ) => Promise<MeetingType | null>;
  setMeetingType: (meetingType: MeetingType | null) => void;
  fetchAvailableSlots: (
    date: Date,
    timeZone: string,
    meetingTypeOverride?: MeetingType,
    duration?: number
  ) => Promise<TimeSlot[]>;
  prefetchMonthSlots: (
    date: Date,
    timeZone: string,
    duration?: number
  ) => Promise<void>;
  getSlotsForDate: (
    date: Date,
    timeZone: string,
    duration?: number
  ) => TimeSlot[];
  createBooking: (
    formData: BookingFormData,
    selectedTime: TimeSlot,
    timeZone: string
  ) => Promise<any>;
  resetBookingState: () => void;
  clearCachedData: () => void;
  switchTimezone: (
    newTimezone: string,
    duration?: number,
    selectedDate?: Date
  ) => Promise<TimeSlot[]>;

  // Provider-specific availability (for service type)
  selectedProviderId?: number | null;
  setSelectedProviderId: (id: number | null | undefined) => void;
}

const BookingDataContext = createContext<BookingDataContextType | undefined>(
  undefined
);

interface BookingDataProviderProps {
  children: ReactNode;
  initialToken?: string | null;
}

/**
 * BookingDataProvider Component
 *
 * Provides a data access layer for booking-related operations,
 * abstracting away API calls and token handling from UI components.
 */
export const BookingDataProvider: React.FC<BookingDataProviderProps> = ({
  children,
  initialToken,
}) => {
  // Token state for API calls
  const [token] = useState<string | null>(initialToken || null);

  // Meeting type state
  const [meetingType, setMeetingType] = useState<MeetingType | null>(null);
  const [loadingMeetingType, setLoadingMeetingType] = useState<boolean>(false);
  const [meetingTypeError, setMeetingTypeError] = useState<string | null>(null);

  // Slots loading state (removed global availableSlots to fix cache collision)
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  const [loadingInitialCalendar, setLoadingInitialCalendar] = useState<boolean>(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  // Cached slot data
  const [cachedMonthSlots, setCachedMonthSlots] = useState<
    Record<string, CachedSlotData>
  >({});

  // First available month (for calendar initial display)
  const [firstAvailableMonth, setFirstAvailableMonth] = useState<Date | null>(null);

  // Track if firstAvailableMonth was set from the authoritative batch API hint (nextAvailability)
  // When set from the API, skip redundant recalculations from cachedMonthSlots changes
  const firstAvailableMonthSetRef = useRef(false);

  // Track the current duration being used
  const currentDurationRef = useRef<number | null>(null);

  // Use a ref to track which months are currently being fetched to prevent duplicate requests
  const monthsBeingFetched = useRef<Set<string>>(new Set());

  // AbortController for request cancellation (not used during timeZone switching)
  const currentRequestController = useRef<AbortController | null>(null);

  // Track current timeZone switch to prevent race conditions
  const currentTimezoneSwitch = useRef<string | null>(null);

  // Flag to track timeZone switching state to prevent request cancellations
  const isTimezoneSwitch = useRef<boolean>(false);

  // Booking state
  const [bookingInProgress, setBookingInProgress] = useState<boolean>(false);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingResponse, setBookingResponse] = useState<any>(null);

  // Provider-specific availability (for service type)
  // undefined = not set, null = "No Preference", number = specific provider userId
  const [selectedProviderId, setSelectedProviderIdState] = useState<number | null | undefined>(undefined);

  /**
   * Fetch meeting type data by username and slug
   */
  const fetchMeetingType = useCallback(
    async (username: string, slug: string): Promise<MeetingType | null> => {
      setLoadingMeetingType(true);
      setMeetingTypeError(null);

      try {
        const data = await meetingTypeService.getMeetingTypeBySlug(
          username,
          slug
        );
        setMeetingType(data);
        return data;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to load meeting type";
        setMeetingTypeError(errorMessage);
        return null;
      } finally {
        setLoadingMeetingType(false);
      }
    },
    []
  );

  /**
   * Fetch available time slots for a specific date
   */
  const fetchAvailableSlots = useCallback(
    async (
      date: Date,
      timeZone: string,
      meetingTypeOverride?: MeetingType,
      duration?: number
    ): Promise<TimeSlot[]> => {
      const effectiveMeetingType = meetingTypeOverride || meetingType;

      if (!effectiveMeetingType) {
        return [];
      }

      // FIXED: Use TimezoneService for proper date range coordination
      // This fixes the "Sept 20 click shows Sept 19 timeslots" issue
      const { startDate: formattedStartDate, endDate: formattedEndDate } =
        TimezoneService.createAPIDateRangeFromClick(date, timeZone);

      // Generate a cache key for this specific date, timeZone, and duration
      // SIMPLE FIX: Use original direct date properties approach
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() is 0-indexed, we need 1-indexed

      // Always include duration in the key for consistency
      const effectiveDuration =
        duration || effectiveMeetingType.duration?.[0] || 30;

      // Use helper function to generate cache key (includes advanced settings for preview mode)
      const monthKey = generateCacheKey(
        effectiveMeetingType.id,
        year,
        month,
        timeZone,
        effectiveDuration,
        {
          bufferTimeBefore: effectiveMeetingType.bufferTimeBefore,
          bufferTimeAfter: effectiveMeetingType.bufferTimeAfter,
          slotIncrementMinutes: effectiveMeetingType.slotIncrementMinutes,
          minimumBookingNotice: effectiveMeetingType.minimumBookingNotice,
          maximumBookingWindow: effectiveMeetingType.maximumBookingWindow,
        },
        effectiveMeetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
        selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
      );
      const dateKey = formattedStartDate;

      // Store current duration for getSlotsForDate to use
      currentDurationRef.current = effectiveDuration;


      // Check if we have a cached version AND if we are loading slots for another request
      if (loadingSlots) {

        // Return cached data if available
        if (
          cachedMonthSlots[monthKey] &&
          cachedMonthSlots[monthKey].slots &&
          cachedMonthSlots[monthKey].slots[dateKey] &&
          cachedMonthSlots[monthKey].isComplete &&
          cachedMonthSlots[monthKey].timeZone === timeZone // Ensure timeZone matches exactly
        ) {
          return cachedMonthSlots[monthKey].slots[dateKey];
        }

        return [];
      }

      // Check if we already have fetched data for this specific day and timeZone
      if (
        cachedMonthSlots[monthKey] &&
        cachedMonthSlots[monthKey].slots &&
        cachedMonthSlots[monthKey].slots[dateKey] &&
        cachedMonthSlots[monthKey].isComplete &&
        cachedMonthSlots[monthKey].timeZone === timeZone // Ensure timeZone matches exactly
      ) {

        const cachedSlots = cachedMonthSlots[monthKey].slots[dateKey];
        return cachedSlots;
      }

      // Check if this date is currently being fetched via prefetchMonthSlots
      if (monthsBeingFetched.current.has(monthKey)) {

        // We'll still return an empty array for now, but we won't initiate a new fetch
        // The UI will show a loading state, and when the prefetch completes, it will update
        setLoadingSlots(true);

        // Return a promise that resolves when the prefetch completes
        return new Promise((resolve) => {
          // Create a timeout to check for the data every 200ms
          const checkInterval = setInterval(() => {
            if (
              cachedMonthSlots[monthKey] &&
              cachedMonthSlots[monthKey].slots &&
              cachedMonthSlots[monthKey].slots[dateKey] &&
              cachedMonthSlots[monthKey].isComplete
            ) {
              clearInterval(checkInterval);
              setLoadingSlots(false);
              resolve(cachedMonthSlots[monthKey].slots[dateKey]);
            } else if (!monthsBeingFetched.current.has(monthKey)) {
              // If the month is no longer being fetched (possibly due to an error)
              // but we don't have data, clear the interval and resolve with empty array
              clearInterval(checkInterval);
              setLoadingSlots(false);
              resolve([]);
            }
          }, 200);

          // Set a timeout to prevent hanging indefinitely
          setTimeout(() => {
            clearInterval(checkInterval);
            setLoadingSlots(false);
            resolve([]);
          }, 10000); // 10 second maximum wait
        });
      }

      try {
        setLoadingSlots(true);
        setSlotsError(null);


        // TimezoneService already created both start and end dates
        // No additional processing needed

        // Check if we already have cached data for this specific day
        // Note: monthKey already includes duration now
        if (
          cachedMonthSlots[monthKey] &&
          cachedMonthSlots[monthKey].slots &&
          cachedMonthSlots[monthKey].slots[dateKey] &&
          cachedMonthSlots[monthKey].isComplete &&
          cachedMonthSlots[monthKey].timeZone === timeZone
        ) {

          const cachedSlots = cachedMonthSlots[monthKey].slots[dateKey];
          setLoadingSlots(false);
          return cachedSlots;
        }

        // If we have an error state saved for this date, don't retry endlessly
        if (
          cachedMonthSlots[monthKey] &&
          cachedMonthSlots[monthKey].error &&
          cachedMonthSlots[monthKey].isComplete &&
          cachedMonthSlots[monthKey].timeZone === timeZone
        ) {

          setLoadingSlots(false);
          return [];
        }

        // Make API call with proper start/end dates


        // PREVIEW MODE: Pass advanced settings as query parameters if in preview mode (id === -1)
        const isPreviewMode = effectiveMeetingType.id === -1;
        const previewParams = isPreviewMode ? {
          bufferTimeBefore: effectiveMeetingType.bufferTimeBefore?.toString(),
          bufferTimeAfter: effectiveMeetingType.bufferTimeAfter?.toString(),
          slotIncrementMinutes: effectiveMeetingType.slotIncrementMinutes?.toString(),
          minimumBookingNotice: effectiveMeetingType.minimumBookingNotice?.toString(),
          maximumBookingWindow: effectiveMeetingType.maximumBookingWindow?.toString(),
          scheduleId: effectiveMeetingType.scheduleId?.toString(),
          // CUSTOM SCHEDULE: Pass customScheduleData when scheduleId is null
          ...(effectiveMeetingType.scheduleId === null && effectiveMeetingType.activeSchedule?.defaultWorkWeek && {
            customScheduleData: JSON.stringify({
              defaultWorkWeek: effectiveMeetingType.activeSchedule.defaultWorkWeek,
              timeZone: effectiveMeetingType.activeSchedule.timeZone || effectiveMeetingType.timeZone
            })
          })
        } : {};

        const response = await axios.get(
          `${API_URL}/api/meeting-types/${effectiveMeetingType.id}/available-slots`,
          {
            params: {
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              timezone: timeZone, // CRITICAL FIX: Backend expects lowercase 'timezone', not 'timeZone'
              ...(duration && { duration: duration.toString() }),
              ...previewParams, // Include preview settings if in preview mode
              // Service type: filter by specific provider
              ...(selectedProviderId !== undefined && selectedProviderId !== null && {
                providerId: selectedProviderId.toString(),
              }),
            },
            headers: getAuthHeaders(),
            // Longer timeout during timeZone switching to prevent premature cancellation
            timeout: isTimezoneSwitch.current ? 30000 : 10000,
            // DO NOT use AbortController signal to prevent timeZone switch cancellation
            // signal: currentRequestController.current?.signal,
          }
        );



        // Process the response into slot objects with timeZone info
        const processedSlots = response.data.map((slot: any) => ({
          ...slot,
          // Ensure timeZone is explicitly set on each slot for identification
          timeZone: timeZone,
          // Ensure Date objects for start and end
          start: new Date(slot.start),
          end: new Date(slot.end),
        }));

        // Slots are now cached and ready for retrieval

        // Update the cache with the response - ensure timeZone is saved with slots
        setCachedMonthSlots((prev) => {
          // Create or get the current monthData
          const currentMonthData = prev[monthKey] || {
            slots: {},
            fetchedAt: new Date().toISOString(),
            isComplete: false,
            error: null,
            timeZone: timeZone, // Store timeZone with the cache entry
          };

          // Create a shallow copy of the slots object to avoid modifying the previous state
          const updatedSlots = { ...currentMonthData.slots };

          // Add the date-specific slots
          updatedSlots[dateKey] = processedSlots;

          // Return the updated state with the new month data
          return {
            ...prev,
            [monthKey]: {
              ...currentMonthData,
              slots: updatedSlots,
              timeZone: timeZone, // Ensure timeZone is set
              isComplete: true,
              fetchedAt: new Date().toISOString(),
            },
          };
        });

        setLoadingSlots(false);
        return processedSlots;
      } catch (error) {
        const errorMessage = handleApiError(error);
        setSlotsError(errorMessage);

        // Cache the error state too to prevent endless retries
        const effectiveDurationForError =
          duration || effectiveMeetingType.duration?.[0] || 30;
        const errorMonthKey = generateCacheKey(
          effectiveMeetingType.id,
          date.getFullYear(),
          date.getMonth() + 1,
          timeZone,
          effectiveDurationForError,
          {
            bufferTimeBefore: effectiveMeetingType.bufferTimeBefore,
            bufferTimeAfter: effectiveMeetingType.bufferTimeAfter,
            slotIncrementMinutes: effectiveMeetingType.slotIncrementMinutes,
            minimumBookingNotice: effectiveMeetingType.minimumBookingNotice,
            maximumBookingWindow: effectiveMeetingType.maximumBookingWindow,
          },
          effectiveMeetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
          selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
        );
        // Note: errorDateKey removed as it was unused

        setCachedMonthSlots((prev) => {
          const existingMonth = prev[errorMonthKey] || {
            slots: {},
            isComplete: false,
          };

          return {
            ...prev,
            [errorMonthKey]: {
              ...existingMonth,
              error: errorMessage,
              isComplete: true, // Mark as complete even on error
              fetchedAt: new Date().toISOString(),
              lastUpdated: Date.now(),
              timeZone: timeZone,
            },
          };
        });

        setLoadingSlots(false);

        // Return empty array instead of fallback slots
        return [];
      }
    },
    [
      meetingType,
      API_URL,
      handleApiError,
      cachedMonthSlots,
      setCachedMonthSlots,
      selectedProviderId,
    ]
  );

  /**
   * Prefetch slots for an entire month
   * SIMPLE FIX: Use original approach but ensure proper cache invalidation
   */
  const prefetchMonthSlots = useCallback(
    async (date: Date, timeZone: string, duration?: number): Promise<void> => {
      if (!meetingType?.id) {
        return;
      }

      try {
        // SIMPLE FIX: Use direct date properties like original
        // The input date should already represent the correct month the user is viewing
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // getMonth() is 0-indexed, we need 1-indexed

        // Create a key for this month's data that includes timeZone and duration
        const effectiveDuration = duration || meetingType.duration?.[0] || 30;
        const monthKey = generateCacheKey(
          meetingType.id,
          year,
          month,
          timeZone,
          effectiveDuration,
          {
            bufferTimeBefore: meetingType.bufferTimeBefore,
            bufferTimeAfter: meetingType.bufferTimeAfter,
            slotIncrementMinutes: meetingType.slotIncrementMinutes,
            minimumBookingNotice: meetingType.minimumBookingNotice,
            maximumBookingWindow: meetingType.maximumBookingWindow,
          },
          meetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
          selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
        );

        // Update current duration ref
        currentDurationRef.current = effectiveDuration;

        // Check if we already have this month's data for this timeZone
        // or if it's already being fetched
        if (
          cachedMonthSlots[monthKey] &&
          cachedMonthSlots[monthKey].isComplete &&
          cachedMonthSlots[monthKey].timeZone === timeZone // Ensure timeZone matches
        ) {
          return;
        }

        // Check if this month is already being fetched - CRITICAL CHECK
        if (monthsBeingFetched.current.has(monthKey)) {
          return;
        }

        // Mark this month as being fetched
        monthsBeingFetched.current.add(monthKey);

        // Create a fresh date object for the first day of the month
        // to avoid potential timeZone issues - USE LAST DAY to avoid date="same date" issues
        const firstDayDate = new Date(Date.UTC(year, month - 1, 1));
        const lastDayDate = new Date(Date.UTC(year, month, 0));

        // Format dates for the API request
        const startDate = firstDayDate.toISOString().split("T")[0];
        const endDate = lastDayDate.toISOString().split("T")[0];

        // Additional sanity check for date range
        if (new Date(startDate) >= new Date(endDate)) {
          throw new Error(
            `Invalid date range: start (${startDate}) must be before end (${endDate})`
          );
        }


        // Mark this month as being fetched in the state but don't update UI
        // until we have actual data
        setCachedMonthSlots((prev) => {
          // Double-check we don't already have this data to prevent race conditions
          if (prev[monthKey] && prev[monthKey].isComplete) {
            return prev;
          }

          return {
            ...prev,
            [monthKey]: {
              slots: {},
              fetchedAt: new Date().toISOString(),
              isComplete: false,
              isLoading: true,
              error: null,
              timeZone: timeZone, // Store timeZone with the cache
              date: startDate, // For backward compatibility
              lastUpdated: Date.now(), // For backward compatibility
            },
          };
        });

        // PREVIEW MODE: Pass advanced settings as query parameters if in preview mode (id === -1)
        const isPreviewMode = meetingType.id === -1;
        const previewParams = isPreviewMode ? {
          bufferTimeBefore: meetingType.bufferTimeBefore?.toString(),
          bufferTimeAfter: meetingType.bufferTimeAfter?.toString(),
          slotIncrementMinutes: meetingType.slotIncrementMinutes?.toString(),
          minimumBookingNotice: meetingType.minimumBookingNotice?.toString(),
          maximumBookingWindow: meetingType.maximumBookingWindow?.toString(),
          scheduleId: meetingType.scheduleId?.toString(),
          // CUSTOM SCHEDULE: Pass customScheduleData when scheduleId is null
          ...(meetingType.scheduleId === null && meetingType.activeSchedule?.defaultWorkWeek && {
            customScheduleData: JSON.stringify({
              defaultWorkWeek: meetingType.activeSchedule.defaultWorkWeek,
              timeZone: meetingType.activeSchedule.timeZone || meetingType.timeZone
            })
          })
        } : {};

        // Fetch the month's data directly
        const response = await axios.get(
          `${API_URL}/api/meeting-types/${meetingType.id}/available-slots`,
          {
            params: {
              startDate,
              endDate,
              timezone: timeZone, // CRITICAL FIX: Backend expects lowercase 'timezone', not 'timeZone'
              ...(effectiveDuration && {
                duration: effectiveDuration.toString(),
              }),
              ...previewParams, // Include preview settings if in preview mode
              // Service type: filter by specific provider
              ...(selectedProviderId !== undefined && selectedProviderId !== null && {
                providerId: selectedProviderId.toString(),
              }),
            },
            headers: getAuthHeaders(token),
            // Longer timeout during timeZone switching to prevent premature cancellation
            timeout: isTimezoneSwitch.current ? 30000 : 15000,
            // DO NOT use AbortController signal for prefetchMonthSlots to prevent timeZone switch cancellation
            // signal: currentRequestController.current?.signal,
          }
        );

        if (!response.data) {
          throw new Error("No data received from API");
        }

        // Process the data by date
        const slotsByDate: Record<string, TimeSlot[]> = {};

        response.data.forEach((slot: any) => {
          try {
            // Ensure we're working with valid Date objects
            const parsedStart =
              typeof slot.start === "string"
                ? parseISO(slot.start)
                : new Date(slot.start);
            const parsedEnd =
              typeof slot.end === "string"
                ? parseISO(slot.end)
                : new Date(slot.end);

            if (isNaN(parsedStart.getTime()) || isNaN(parsedEnd.getTime())) {
              return; // Skip this slot
            }

            // Get the date string in YYYY-MM-DD format based on timeZone
            // CRITICAL FIX: Use manual date extraction to match cacheManager.ts
            const zonedStart = utcToZonedTime(parsedStart, timeZone);
            const slotYear = zonedStart.getFullYear();
            const slotMonth = String(zonedStart.getMonth() + 1).padStart(2, '0');
            const slotDay = String(zonedStart.getDate()).padStart(2, '0');
            const dateKey = `${slotYear}-${slotMonth}-${slotDay}`;

            if (!slotsByDate[dateKey]) {
              slotsByDate[dateKey] = [];
            }

            slotsByDate[dateKey].push({
              start: parsedStart,
              end: parsedEnd,
              timeZone, // Store timeZone with each slot
            });
          } catch (error) {
            // Error handled silently
          }
        });

        // Update the cached data
        setCachedMonthSlots((prev) => ({
          ...prev,
          [monthKey]: {
            slots: slotsByDate,
            fetchedAt: new Date().toISOString(),
            isComplete: true,
            isLoading: false,
            error: null,
            timeZone: timeZone, // Ensure timeZone is stored
            date: startDate, // For backward compatibility
            lastUpdated: Date.now(), // For backward compatibility
          },
        }));

      } catch (error: any) {
        // Handle error but don't disrupt the UI

        // Store detailed error information for debugging
        const errorDetails = {
          message: error.message || "Unknown error",
          response: error.response
            ? {
                status: error.response.status,
                data: error.response.data,
              }
            : "No response",
          stack: error.stack,
        };

        const effectiveDurationForError =
          duration || meetingType.duration?.[0] || 30;
        const errorMonthKey = generateCacheKey(
          meetingType.id,
          date.getFullYear(),
          date.getMonth() + 1,
          timeZone,
          effectiveDurationForError,
          {
            bufferTimeBefore: meetingType.bufferTimeBefore,
            bufferTimeAfter: meetingType.bufferTimeAfter,
            slotIncrementMinutes: meetingType.slotIncrementMinutes,
            minimumBookingNotice: meetingType.minimumBookingNotice,
            maximumBookingWindow: meetingType.maximumBookingWindow,
          },
          meetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
          selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
        );

        setCachedMonthSlots((prev) => ({
          ...prev,
          [errorMonthKey]: {
            slots: {},
            fetchedAt: new Date().toISOString(),
            isComplete: true, // Mark as complete to prevent repeated requests
            isLoading: false,
            error: JSON.stringify(errorDetails),
            timeZone: timeZone, // Ensure timeZone is stored
            date: date.toISOString().split("T")[0], // For backward compatibility
            lastUpdated: Date.now(), // For backward compatibility
          },
        }));
      } finally {
        // IMPORTANT: Always remove this month from the set of months being fetched
        // even if there was an error, to prevent deadlock
        const effectiveDurationFinal =
          duration || meetingType.duration?.[0] || 30;
        const monthKey = generateCacheKey(
          meetingType.id,
          date.getFullYear(),
          date.getMonth() + 1,
          timeZone,
          effectiveDurationFinal,
          {
            bufferTimeBefore: meetingType.bufferTimeBefore,
            bufferTimeAfter: meetingType.bufferTimeAfter,
            slotIncrementMinutes: meetingType.slotIncrementMinutes,
            minimumBookingNotice: meetingType.minimumBookingNotice,
            maximumBookingWindow: meetingType.maximumBookingWindow,
          },
          meetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
          selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
        );

        // Add a small delay before removing from the fetching set
        // to prevent immediate retries - reduced from 2000ms to 500ms for better responsiveness
        setTimeout(() => {
          monthsBeingFetched.current.delete(monthKey);
        }, 500);
      }
    },
    [meetingType, cachedMonthSlots, setCachedMonthSlots, token, selectedProviderId]
  );

  /**
   * Get slots for a specific date from cache
   * SIMPLE FIX: Use the original cache key method but ensure timeZone consistency
   */
  const getSlotsForDate = useCallback(
    (date: Date, timeZone: string, duration?: number): TimeSlot[] => {
      if (!meetingType?.id) {
        return [];
      }

      // CRITICAL FIX: Use the same simple approach as original but clear cache on timeZone change
      // Use direct date properties like original code
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() is 0-indexed, we need 1-indexed

      // Use the same duration logic as other functions
      const effectiveDuration =
        duration ||
        currentDurationRef.current ||
        meetingType.duration?.[0] ||
        30;
      const monthKey = generateCacheKey(
        meetingType.id,
        year,
        month,
        timeZone,
        effectiveDuration,
        {
          bufferTimeBefore: meetingType.bufferTimeBefore,
          bufferTimeAfter: meetingType.bufferTimeAfter,
          slotIncrementMinutes: meetingType.slotIncrementMinutes,
          minimumBookingNotice: meetingType.minimumBookingNotice,
          maximumBookingWindow: meetingType.maximumBookingWindow,
        },
        meetingType.scheduleId,  // CRITICAL: Include scheduleId to prevent cache collisions
        selectedProviderId  // CRITICAL: Include providerId to prevent cache collisions when switching providers
      );

      // FIXED: Normalize clicked date to avoid timeZone boundary issues
      // Create a normalized date at noon in the target timeZone to match server logic
      const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
      const dateInTargetTimezone = utcToZonedTime(normalizedDate, timeZone);
      const dateYear = dateInTargetTimezone.getFullYear();
      const dateMonth = String(dateInTargetTimezone.getMonth() + 1).padStart(2, '0');
      const dateDay = String(dateInTargetTimezone.getDate()).padStart(2, '0');
      const dateKey = `${dateYear}-${dateMonth}-${dateDay}`;


      // Check if we have cached data for this specific date
      if (
        cachedMonthSlots[monthKey] &&
        cachedMonthSlots[monthKey].slots &&
        cachedMonthSlots[monthKey].slots[dateKey] &&
        cachedMonthSlots[monthKey].isComplete &&
        cachedMonthSlots[monthKey].timeZone === timeZone
      ) {
        return cachedMonthSlots[monthKey].slots[dateKey];
      }

      return [];
    },
    [meetingType, cachedMonthSlots, currentDurationRef.current, selectedProviderId]
  );

  /**
   * Get the first available month from cached data
   * Used to auto-jump calendar to first month with available slots
   */
  const getFirstAvailableMonth = useCallback((): Date | null => {
    if (!meetingType?.id || Object.keys(cachedMonthSlots).length === 0) {
      return null;
    }

    // Get all cache keys and sort them chronologically
    // Cache key format: {meetingTypeId}-{year}-{month}-{timezone}-{duration}-{hash}
    // CRITICAL FIX: Handle negative meeting type IDs (e.g., -1 for preview mode)
    // Negative IDs create empty first element when split: "-1-2025-10" → ['', '1', '2025', '10']
    const sortedMonthKeys = Object.keys(cachedMonthSlots).sort((a, b) => {
      // Extract year and month from cache key
      const partsA = a.split('-');
      const partsB = b.split('-');

      // Skip if format is unexpected
      if (partsA.length < 3 || partsB.length < 3) return 0;

      // Handle negative meeting type IDs (which create empty first element)
      const offsetA = partsA[0] === '' ? 1 : 0;
      const offsetB = partsB[0] === '' ? 1 : 0;

      const yearA = parseInt(partsA[1 + offsetA]);
      const monthA = parseInt(partsA[2 + offsetA]);
      const yearB = parseInt(partsB[1 + offsetB]);
      const monthB = parseInt(partsB[2 + offsetB]);

      // Sort by year first, then by month
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    });

    // Find the first month that has available slots
    for (const monthKey of sortedMonthKeys) {
      const monthData = cachedMonthSlots[monthKey];

      // Skip if month has error or is not complete
      if (monthData.error || !monthData.isComplete) {
        continue;
      }

      // Check if this month has any slots
      const hasSlots = Object.values(monthData.slots).some(
        (slots) => Array.isArray(slots) && slots.length > 0
      );

      if (hasSlots) {
        // Extract year and month from cache key
        const parts = monthKey.split('-');

        // CRITICAL FIX: Handle negative meeting type IDs (which create empty first element)
        const offset = parts[0] === '' ? 1 : 0;

        if (parts.length >= 3 + offset) {
          const year = parseInt(parts[1 + offset]);
          const month = parseInt(parts[2 + offset]);

          // Return a date representing the first day of this month
          return new Date(year, month - 1, 1);
        }
      }
    }

    // No months with slots found
    return null;
  }, [meetingType?.id, cachedMonthSlots]);

  /**
   * Create a booking
   */
  const createBooking = useCallback(
    async (
      formData: BookingFormData,
      selectedTime: TimeSlot,
      timeZone: string
    ): Promise<any> => {
      if (!meetingType?.id) {
        setBookingError("Meeting type not loaded");
        return null;
      }

      setBookingInProgress(true);
      setBookingError(null);
      setBookingSuccess(false);

      try {
        // Last-second slot check. The host's calendar is checked server-side
        // (the host token never reaches the browser); this is best-effort and
        // the authoritative conflict check runs again at booking creation.
        if (meetingType.calendarConnection) {
          try {
            const isAvailable = await meetingTypeService.checkSlotAvailable(
              meetingType.id.toString(),
              new Date(selectedTime.start),
              new Date(selectedTime.end)
            );

            if (!isAvailable) {
              setBookingError("This time slot is no longer available");
              setBookingInProgress(false);
              return null;
            }
          } catch (slotCheckError) {
            // Best-effort: never block booking on the pre-check.
          }
        }

        // Create booking request
        const bookingData = {
          meetingTypeId: meetingType.id.toString(),
          name: formData.name,
          email: formData.email,
          startTime: new Date(selectedTime.start),
          timeZone: timeZone,
          notes: formData.notes || "",
          // Add any custom fields
          ...Object.entries(formData)
            .filter(([key]) => !["name", "email", "notes"].includes(key))
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {}),
        };

        // Submit booking
        const response = await bookingService.createBooking(bookingData);

        setBookingSuccess(true);
        setBookingResponse(response);
        return response;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || error.message || "Booking failed";
        setBookingError(errorMessage);
        return null;
      } finally {
        setBookingInProgress(false);
      }
    },
    [meetingType, token]
  );

  /**
   * Reset booking state
   */
  const resetBookingState = useCallback(() => {
    setBookingInProgress(false);
    setBookingSuccess(false);
    setBookingError(null);
    setBookingResponse(null);
  }, []);

  /**
   * Clear cached data for specific timeZone - TARGETED FIX
   */
  const clearCachedData = useCallback((specificTimezone?: string) => {
    if (specificTimezone) {
      setCachedMonthSlots(prev => {
        const newCache: Record<string, CachedSlotData> = {};
        // Keep cache entries that don't match the specific timeZone
        Object.entries(prev).forEach(([key, value]) => {
          if (!key.includes(`-${specificTimezone}-`)) {
            newCache[key] = value;
          }
        });
        return newCache;
      });
    } else {
      setCachedMonthSlots({});
    }
    // Reset first available month when clearing cache
    setFirstAvailableMonth(null);
    firstAvailableMonthSetRef.current = false;
  }, []);

  /**
   * Set the selected provider ID for service-type availability filtering.
   * Clears cached data and refetches when provider changes.
   */
  const setSelectedProviderId = useCallback((id: number | null | undefined) => {
    setSelectedProviderIdState(prev => {
      // No change — skip
      if (prev === id) return prev;
      return id;
    });
  }, []);

  // Clear cache and refetch when selectedProviderId changes
  const prevProviderIdRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    // Skip the initial render (provider not yet set)
    if (prevProviderIdRef.current === undefined && selectedProviderId === undefined) {
      return;
    }
    // Skip if value hasn't actually changed
    if (prevProviderIdRef.current === selectedProviderId) {
      return;
    }
    prevProviderIdRef.current = selectedProviderId;

    // Clear all cached data so the calendar refetches with the new provider filter
    setCachedMonthSlots({});
    monthsBeingFetched.current.clear();
    setFirstAvailableMonth(null);
    firstAvailableMonthSetRef.current = false;

    // Trigger refetch of current month
    if (meetingType?.id) {
      const timeZone = getUserTimezone();
      const today = new Date();
      const effectiveDuration = currentDurationRef.current || meetingType.duration?.[0] || 30;

      const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      prefetchMonthSlots(currentMonth, timeZone, effectiveDuration).catch(() => {});
    }
  }, [selectedProviderId, meetingType?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Simple timeZone switching - clear all cache and refetch fresh data
   * Following Calendly's approach: don't try to preserve cache across timeZones
   */
  const switchTimezone = useCallback(
    async (
      newTimezone: string,
      duration?: number,
      selectedDate?: Date
    ): Promise<TimeSlot[]> => {
      if (!meetingType?.id) {
        return [];
      }

      // Cancel any previous timeZone switch
      if (currentTimezoneSwitch.current && currentTimezoneSwitch.current !== newTimezone) {
        // Cancel previous timezone switch
      }

      // Track this timeZone switch
      currentTimezoneSwitch.current = newTimezone;

      // Step 1: Clear ALL cache - don't try to be clever about preserving data
      setCachedMonthSlots({});
      monthsBeingFetched.current.clear();
      setFirstAvailableMonth(null); // Reset first available month
      firstAvailableMonthSetRef.current = false;

      // Step 2: Set loading state briefly, then clear it to make UI responsive
      setLoadingSlots(true);
      setSlotsError(null);

      // Step 3: Only fetch current month — additional months lazy-load on navigation
      const referenceDate = selectedDate || new Date();
      const effectiveDuration =
        duration ||
        currentDurationRef.current ||
        meetingType.duration?.[0] ||
        30;

      // Fetch only the current month (single API call, fast response from server cache)
      const currentMonth = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        1
      );

      prefetchMonthSlots(currentMonth, newTimezone, effectiveDuration)
        .catch(() => {
          // Error handled silently
        })
        .finally(() => {
          setLoadingSlots(false);
          currentTimezoneSwitch.current = null;
        });

      // Step 7: Return immediate slots if available, empty array otherwise
      const slotsForDate = getSlotsForDate(
        referenceDate,
        newTimezone,
        effectiveDuration
      );

      return slotsForDate;
    },
    [meetingType?.id, clearCachedData, prefetchMonthSlots, getSlotsForDate]
  );

  // Auto-prefetch availability when meeting type is loaded
  useEffect(() => {
    if (!meetingType?.id) return;

    // CRITICAL FIX: Always use browser timezone (getUserTimezone) to match actual booking page behavior
    const timeZone = getUserTimezone();
    const today = new Date();

    // Get the default duration for prefetching
    const defaultDuration = meetingType.duration?.[0] || 30;
    currentDurationRef.current = defaultDuration;

    // Calculate how many months to prefetch based on meeting type's advance booking settings
    const maxAdvanceDays = meetingType.maximumBookingWindow || 60;
    const monthsToPreload = Math.ceil(maxAdvanceDays / 30) + 1;

    // Check if we need to show loading spinner by checking if first month is cached
    const firstMonthKey = generateCacheKey(
      meetingType.id,
      today.getFullYear(),
      today.getMonth() + 1,
      timeZone,
      defaultDuration,
      {
        bufferTimeBefore: meetingType.bufferTimeBefore,
        bufferTimeAfter: meetingType.bufferTimeAfter,
        slotIncrementMinutes: meetingType.slotIncrementMinutes,
        minimumBookingNotice: meetingType.minimumBookingNotice,
        maximumBookingWindow: meetingType.maximumBookingWindow,
      },
      meetingType.scheduleId,
      selectedProviderId
    );

    const firstMonthCached = cachedMonthSlots[firstMonthKey]?.isComplete;

    // Show loading spinner if first month is not yet cached
    if (!firstMonthCached) {
      setLoadingInitialCalendar(true);
    }

    // PREVIEW MODE: Use individual prefetches (batch endpoint doesn't support preview params)
    const isPreviewMode = meetingType.id === -1;

    if (isPreviewMode) {
      const prefetchPromises = [];
      for (let i = 0; i < monthsToPreload; i++) {
        const monthToFetch = new Date(
          today.getFullYear(),
          today.getMonth() + i,
          1
        );
        const prefetchPromise = prefetchMonthSlots(
          monthToFetch,
          timeZone,
          defaultDuration
        ).catch(() => {
          // Error handled silently
        });
        prefetchPromises.push(prefetchPromise);
      }

      if (!firstMonthCached && prefetchPromises.length > 0) {
        prefetchPromises[0].finally(() => {
          setLoadingInitialCalendar(false);
        });
      }

      Promise.allSettled(prefetchPromises).then(() => {});
      return;
    }

    // B1 OPTIMIZATION: Use inline availability from slug response if available.
    // This eliminates the sequential slug → batch-availability waterfall (~5.5s savings).
    if ((meetingType as any).inlineAvailability && !firstMonthCached) {
      const inline = (meetingType as any).inlineAvailability;
      const slotsData = inline.slots || {};
      const nextAvailabilityDate: string | null = inline.nextAvailability || null;

      // Set firstAvailableMonth from inline data
      if (nextAvailabilityDate) {
        const [navYear, navMonth] = nextAvailabilityDate.split('-').map(Number);
        setFirstAvailableMonth(new Date(navYear, navMonth - 1, 1));
        firstAvailableMonthSetRef.current = true;
      }

      // Parse inline slots into the same format as batch response
      const slotsByMonth: Record<string, Record<string, TimeSlot[]>> = {};
      slotsByMonth[firstMonthKey] = {};

      Object.values(slotsData).forEach((dateSlots: any) => {
        if (!Array.isArray(dateSlots)) return;
        dateSlots.forEach((slot: any) => {
          try {
            const parsedStart = typeof slot.start === 'string' ? parseISO(slot.start) : new Date(slot.start);
            const parsedEnd = typeof slot.end === 'string' ? parseISO(slot.end) : new Date(slot.end);
            if (isNaN(parsedStart.getTime()) || isNaN(parsedEnd.getTime())) return;

            const zonedStart = utcToZonedTime(parsedStart, timeZone);
            const slotDay = String(zonedStart.getDate()).padStart(2, '0');
            const slotMonth = zonedStart.getMonth() + 1;
            const slotYear = zonedStart.getFullYear();
            const dateKey = `${slotYear}-${String(slotMonth).padStart(2, '0')}-${slotDay}`;

            if (!slotsByMonth[firstMonthKey][dateKey]) {
              slotsByMonth[firstMonthKey][dateKey] = [];
            }
            slotsByMonth[firstMonthKey][dateKey].push({
              start: parsedStart,
              end: parsedEnd,
              timeZone: timeZone,
            });
          } catch (_e) {
            // Skip malformed slots
          }
        });
      });

      setCachedMonthSlots(prev => ({
        ...prev,
        [firstMonthKey]: {
          slots: slotsByMonth[firstMonthKey] || {},
          fetchedAt: new Date().toISOString(),
          isComplete: true,
          isLoading: false,
          error: null,
          timeZone: timeZone,
          lastUpdated: Date.now(),
        },
      }));

      // If inline has slots (nextAvailability is set), we're done — return early.
      // If inline has zero slots (nextAvailability is null), fall through to batch API
      // which has look-ahead logic to find the next available month.
      if (nextAvailabilityDate) {
        setLoadingInitialCalendar(false);
        return;
      }
      // Zero slots in current month — fall through to batch API for look-ahead
    }

    // NORMAL MODE: Use batch endpoint - fetch current month only (single API call).
    // Additional months lazy-load on calendar navigation via prefetchMonthSlots.
    // The batch response includes nextAvailability to enable immediate calendar auto-navigation.
    // Helper: Build date ranges and month keys for a given range of month offsets
    const buildMonthBatch = (startOffset: number, endOffset: number) => {
      const dateRanges: { start: string; end: string }[] = [];
      const monthKeys: string[] = [];
      const monthDates: Date[] = [];

      for (let i = startOffset; i < endOffset; i++) {
        const monthDate = new Date(Date.UTC(today.getFullYear(), today.getMonth() + i, 1));
        const lastDay = new Date(Date.UTC(today.getFullYear(), today.getMonth() + i + 1, 0));
        const year = monthDate.getUTCFullYear();
        const month = monthDate.getUTCMonth() + 1;

        const monthKey = generateCacheKey(
          meetingType.id,
          year,
          month,
          timeZone,
          defaultDuration,
          {
            bufferTimeBefore: meetingType.bufferTimeBefore,
            bufferTimeAfter: meetingType.bufferTimeAfter,
            slotIncrementMinutes: meetingType.slotIncrementMinutes,
            minimumBookingNotice: meetingType.minimumBookingNotice,
            maximumBookingWindow: meetingType.maximumBookingWindow,
          },
          meetingType.scheduleId,
          selectedProviderId
        );

        // Skip if already cached or being fetched
        if (
          (cachedMonthSlots[monthKey]?.isComplete && cachedMonthSlots[monthKey]?.timeZone === timeZone) ||
          monthsBeingFetched.current.has(monthKey)
        ) {
          continue;
        }

        dateRanges.push({
          start: monthDate.toISOString().split('T')[0],
          end: lastDay.toISOString().split('T')[0],
        });
        monthKeys.push(monthKey);
        monthDates.push(monthDate);
      }

      return { dateRanges, monthKeys, monthDates };
    };

    // Reusable batch fetch executor
    const executeBatchFetch = async (
      dateRanges: { start: string; end: string }[],
      batchMonthKeys: string[],
      batchMonthDates: Date[],
    ) => {
      if (dateRanges.length === 0) return;

      // Mark months as being fetched
      batchMonthKeys.forEach(key => monthsBeingFetched.current.add(key));

      // Set loading state for uncached months
      setCachedMonthSlots(prev => {
        const updated = { ...prev };
        batchMonthKeys.forEach(key => {
          if (!updated[key]?.isComplete) {
            updated[key] = {
              slots: {},
              fetchedAt: new Date().toISOString(),
              isComplete: false,
              isLoading: true,
              error: null,
              timeZone: timeZone,
              lastUpdated: Date.now(),
            };
          }
        });
        return updated;
      });

      try {
        const response = await axios.get(
          `${API_URL}/api/meeting-types/${meetingType.id}/batch-availability`,
          {
            params: {
              ranges: JSON.stringify(dateRanges),
              timezone: timeZone,
              ...(selectedProviderId !== undefined && selectedProviderId !== null && {
                providerId: selectedProviderId.toString(),
              }),
            },
            headers: getAuthHeaders(token),
            timeout: 30000,
          }
        );

        if (!response.data) {
          throw new Error('No data received from batch API');
        }

        // Parse wrapped response: { slots: {...}, nextAvailability: "YYYY-MM-DD" | null }
        const batchResponse = response.data;
        const slotsData = batchResponse.slots || batchResponse; // Handle wrapped format
        const nextAvailabilityDate: string | null = batchResponse.nextAvailability || null;

        // Set firstAvailableMonth immediately from nextAvailability hint
        // This eliminates the need to fetch all months to find the first available date
        if (nextAvailabilityDate) {
          const [navYear, navMonth] = nextAvailabilityDate.split('-').map(Number);
          setFirstAvailableMonth(new Date(navYear, navMonth - 1, 1));
          firstAvailableMonthSetRef.current = true;
        }

        // Process batch response: group all slots by timezone-adjusted month and date
        const slotsByMonth: Record<string, Record<string, TimeSlot[]>> = {};

        // Initialize all requested month buckets (ensures months with 0 slots are marked complete)
        batchMonthKeys.forEach(key => {
          slotsByMonth[key] = {};
        });

        // Flatten and re-group all slots by timezone-adjusted date
        Object.values(slotsData).forEach((dateSlots: any) => {
          if (!Array.isArray(dateSlots)) return;

          dateSlots.forEach((slot: any) => {
            try {
              const parsedStart = typeof slot.start === 'string' ? parseISO(slot.start) : new Date(slot.start);
              const parsedEnd = typeof slot.end === 'string' ? parseISO(slot.end) : new Date(slot.end);

              if (isNaN(parsedStart.getTime()) || isNaN(parsedEnd.getTime())) return;

              const zonedStart = utcToZonedTime(parsedStart, timeZone);
              const slotYear = zonedStart.getFullYear();
              const slotMonth = zonedStart.getMonth() + 1;
              const slotDay = String(zonedStart.getDate()).padStart(2, '0');
              const dateKey = `${slotYear}-${String(slotMonth).padStart(2, '0')}-${slotDay}`;

              const slotMonthKey = generateCacheKey(
                meetingType.id,
                slotYear,
                slotMonth,
                timeZone,
                defaultDuration,
                {
                  bufferTimeBefore: meetingType.bufferTimeBefore,
                  bufferTimeAfter: meetingType.bufferTimeAfter,
                  slotIncrementMinutes: meetingType.slotIncrementMinutes,
                  minimumBookingNotice: meetingType.minimumBookingNotice,
                  maximumBookingWindow: meetingType.maximumBookingWindow,
                },
                meetingType.scheduleId,
                selectedProviderId
              );

              if (!slotsByMonth[slotMonthKey]) {
                slotsByMonth[slotMonthKey] = {};
              }
              if (!slotsByMonth[slotMonthKey][dateKey]) {
                slotsByMonth[slotMonthKey][dateKey] = [];
              }

              slotsByMonth[slotMonthKey][dateKey].push({
                start: parsedStart,
                end: parsedEnd,
                timeZone: timeZone,
              });
            } catch (error) {
              // Error handled silently
            }
          });
        });

        // Update cache with all month data in a single state update
        setCachedMonthSlots(prev => {
          const updated = { ...prev };
          batchMonthKeys.forEach(key => {
            updated[key] = {
              slots: slotsByMonth[key] || {},
              fetchedAt: new Date().toISOString(),
              isComplete: true,
              isLoading: false,
              error: null,
              timeZone: timeZone,
              lastUpdated: Date.now(),
            };
          });
          return updated;
        });

      } catch (error: any) {
        // Clear month keys from being-fetched set so individual fetches can proceed
        batchMonthKeys.forEach(key => monthsBeingFetched.current.delete(key));

        // Fall back to individual prefetches
        const fallbackPromises = batchMonthDates.map((monthDate, i) =>
          prefetchMonthSlots(monthDate, timeZone, defaultDuration)
            .catch(() => { /* Error handled silently */ })
        );

        await Promise.allSettled(fallbackPromises);
      } finally {
        // Remove months from being-fetched set after a brief delay
        setTimeout(() => {
          batchMonthKeys.forEach(key => monthsBeingFetched.current.delete(key));
        }, 500);
      }
    };

    // Only fetch the current month on initial load (single API call).
    // Additional months are lazy-loaded when the user navigates the calendar.
    // The batch response includes nextAvailability so the calendar knows where
    // to auto-navigate without prefetching all months upfront.
    const initialBatch = buildMonthBatch(0, 1);

    if (initialBatch.dateRanges.length === 0) {
      setLoadingInitialCalendar(false);
      return;
    }

    const initialFetch = async () => {
      try {
        await executeBatchFetch(initialBatch.dateRanges, initialBatch.monthKeys, initialBatch.monthDates);
      } finally {
        setLoadingInitialCalendar(false);
      }
    };

    initialFetch();
  }, [
    meetingType?.id,
    // CRITICAL: For preview mode (id === -1), include advanced settings in dependencies
    // This ensures auto-prefetch refires when any setting changes in preview
    meetingType?.id === -1 ? meetingType?.bufferTimeBefore : undefined,
    meetingType?.id === -1 ? meetingType?.bufferTimeAfter : undefined,
    meetingType?.id === -1 ? meetingType?.slotIncrementMinutes : undefined,
    meetingType?.id === -1 ? meetingType?.minimumBookingNotice : undefined,
    meetingType?.id === -1 ? meetingType?.maximumBookingWindow : undefined,
    meetingType?.id === -1 ? meetingType?.scheduleId : undefined,  // CRITICAL: Include scheduleId for preview mode
    // CRITICAL FIX: Track custom schedule changes for preview mode
    // When scheduleId is null (custom schedule), stringify entire activeSchedule (includes defaultWorkWeek AND timeZone)
    meetingType?.id === -1 && meetingType?.scheduleId === null
      ? JSON.stringify(meetingType?.activeSchedule)
      : undefined,
  ]); // REMOVED prefetchMonthSlots from dependencies to prevent timeZone switch interference

  // Cleanup: Cancel any ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      if (currentRequestController.current) {
        currentRequestController.current.abort();
      }
    };
  }, []);

  // Calculate first available month whenever cached slots change
  // Skip if already set from the batch API's authoritative nextAvailability hint
  useEffect(() => {
    if (!meetingType?.id) {
      setFirstAvailableMonth(null);
      firstAvailableMonthSetRef.current = false;
      return;
    }

    // Skip recalculation if already set from batch API's nextAvailability hint
    if (firstAvailableMonthSetRef.current) {
      return;
    }

    // Check if we have any complete cached months
    const hasCompleteCachedData = Object.values(cachedMonthSlots).some(
      (monthData) => monthData.isComplete && !monthData.error
    );

    if (!hasCompleteCachedData) {
      // No complete data yet, keep firstAvailableMonth as null
      return;
    }

    // Recalculate first available month
    const firstMonth = getFirstAvailableMonth();
    if (firstMonth) {
      setFirstAvailableMonth(firstMonth);
    } else {
      setFirstAvailableMonth(null);
    }
  }, [cachedMonthSlots, meetingType?.id, getFirstAvailableMonth]);

  const contextValue = {
    // Meeting type data
    meetingType,
    loadingMeetingType,
    meetingTypeError,

    // Slots loading state
    loadingSlots,
    loadingInitialCalendar,
    slotsError,

    // Cached slot data
    cachedMonthSlots,

    // First available month
    firstAvailableMonth,

    // Booking state
    bookingInProgress,
    bookingSuccess,
    bookingError,
    bookingResponse,

    // Methods
    fetchMeetingType,
    setMeetingType,
    fetchAvailableSlots,
    prefetchMonthSlots,
    getSlotsForDate,
    createBooking,
    resetBookingState,
    clearCachedData,
    switchTimezone,

    // Provider-specific availability
    selectedProviderId,
    setSelectedProviderId,
  };

  return (
    <BookingDataContext.Provider value={contextValue}>
      {children}
    </BookingDataContext.Provider>
  );
};

/**
 * useBookingData Hook
 *
 * Provides access to booking data and methods
 */
export const useBookingData = (): BookingDataContextType => {
  const context = useContext(BookingDataContext);

  if (!context) {
    throw new Error("useBookingData must be used within a BookingDataProvider");
  }

  return context;
};
